'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { arbitralDetalhesService } from '../store/services';
import type { CobrancaProcessoClienteApi, FinanceiroProcessoCliente, ItemConsumoCliente } from '../types/financeiroProcessoClienteTypes';
interface UseFinanceiroProcessoResult {
  /** Resumo agregado no servidor. `null` enquanto não se sabe. */
  financeiro: FinanceiroProcessoCliente | null;
  cobrancas: CobrancaProcessoClienteApi[];
  extrato: ItemConsumoCliente[];
  carregando: boolean;
  erro: string | null;
  recarregar: () => void;
}

/**
 * Financeiro do processo na projeção do cliente — contrato v2.
 *
 * Duas requisições, não uma: o **resumo** vem agregado no banco sobre a coleção
 * inteira, e a **coleção** vem paginada. Enquanto tudo viajava num payload só, o
 * navegador somava as linhas que recebeu e chamava aquilo de total — com régua
 * própria, que divergia do servidor em `ERRO_EMISSAO`, e com teto de página que
 * faria o total encolher em silêncio num processo grande.
 *
 * A coleção buscada depende da modalidade: avulso tem cobranças, conveniado tem
 * consumo do ciclo. Não são a mesma coisa com nomes diferentes — no convênio não
 * existe cobrança por processo.
 *
 * Os três estados (carregando / erro / dado) são devolvidos SEPARADOS: engoli-los
 * num `null` só fazia a tela dizer "nenhuma cobrança" durante o carregamento e
 * também quando o servidor estava fora.
 */
export function useFinanceiroProcesso(processoId) {
  const $ = _c(14);
  const [financeiro, setFinanceiro] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [cobrancas, setCobrancas] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [extrato, setExtrato] = useState(t1);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [tentativa, setTentativa] = useState(0);
  const geracao = useRef(0);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      geracao.current = geracao.current + 1;
      setTentativa(_temp);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const recarregar = t2;
  let t3;
  if ($[3] !== processoId) {
    t3 = () => {
      if (!processoId) {
        setFinanceiro(null);
        setCobrancas([]);
        setExtrato([]);
        setErro(null);
        return;
      }
      let cancelado = false;
      const geracaoDaLeitura = geracao.current;
      const obsoleta = () => cancelado || geracao.current !== geracaoDaLeitura;
      setCarregando(true);
      setErro(null);
      const carregar = async () => {
        const resumo = await arbitralDetalhesService.fetchResumoFinanceiro(processoId);
        if (obsoleta()) {
          return;
        }
        setFinanceiro(resumo);
        if (resumo.modalidade === "AVULSO") {
          const pagina = await arbitralDetalhesService.fetchCobrancas(processoId);
          if (obsoleta()) {
            return;
          }
          setCobrancas(pagina.data);
          setExtrato([]);
          return;
        }
        const pagina_0 = await arbitralDetalhesService.fetchExtratoConsumo(processoId);
        if (obsoleta()) {
          return;
        }
        setExtrato(pagina_0.data);
        setCobrancas([]);
      };
      carregar().catch(falha => {
        if (obsoleta()) {
          return;
        }
        setErro(falha instanceof Error && falha.message ? falha.message : "N\xE3o conseguimos carregar o financeiro agora");
      }).finally(() => {
        if (!obsoleta()) {
          setCarregando(false);
        }
      });
      return () => {
        cancelado = true;
      };
    };
    $[3] = processoId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== processoId || $[6] !== tentativa) {
    t4 = [processoId, tentativa];
    $[5] = processoId;
    $[6] = tentativa;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== carregando || $[9] !== cobrancas || $[10] !== erro || $[11] !== extrato || $[12] !== financeiro) {
    t5 = {
      financeiro,
      cobrancas,
      extrato,
      carregando,
      erro,
      recarregar
    };
    $[8] = carregando;
    $[9] = cobrancas;
    $[10] = erro;
    $[11] = extrato;
    $[12] = financeiro;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function _temp(n) {
  return n + 1;
}